import { useState } from 'react';

import { Button } from '@shared/ui/button';
import { Select } from '@shared/ui/select';
import type { SelectOption } from '@shared/ui/select';
import { MyModal } from '@shared/ui/my-modal';
import { PushBadge } from '@entities/marketing-push/ui';
import { formatPushDate, formatPushNumber } from '@entities/marketing-push/lib';
import type { DraftProblem } from '@entities/marketing-push/lib';
import type {
  PushAudienceEstimate,
  PushCampaign,
  PushLanguage,
  PushNotificationType,
  PushTestDevice,
} from '../../../types/marketingPushInterfaces';

const CARD = 'bg-white rounded-xl shadow-sm p-4 mb-4';
const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const selectControlStyles = { control: () => ({ height: '44px', minHeight: '44px' }) };

/* -------------------------------------------------------------------------- */
/* Расчёт получателей                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Расчёт показывается с разбивкой, а не одним числом.
 *
 * Иначе нулевая аудитория выглядит как поломка сервиса, хотя причина обычно
 * в настройках самой рассылки: язык не выбран или у получателей нет согласия
 * на рекламу.
 */
export function PushAudiencePanel({
  estimate,
  loading,
  notificationType,
  languages,
  saveFirst,
  onRefresh,
}: {
  estimate?: PushAudienceEstimate;
  loading?: boolean;
  notificationType?: PushNotificationType;
  languages: PushLanguage[];
  /** Черновик не сохранён — считать нечего. */
  saveFirst?: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className={CARD}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-[#2d2c2c]">Расчёт получателей</h2>
        <Button isWhite disabled={loading || saveFirst} onClick={onRefresh}>
          Пересчитать
        </Button>
      </div>

      {saveFirst && (
        <p className="text-sm text-gray-500">Сохраните черновик, чтобы увидеть расчёт.</p>
      )}

      {!saveFirst && loading && <p className="text-sm text-gray-500">Считаем…</p>}

      {!saveFirst && !loading && estimate && (
        <>
          <div className="mb-3">
            <div className="text-2xl font-semibold leading-tight text-[#2d2c2c]">
              {formatPushNumber(estimate.total)}
            </div>
            <div className="text-xs text-gray-500">{estimate.note}</div>
          </div>

          {estimate.total === 0 && (
            <div className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <strong className="block">Получателей нет</strong>
              Проверьте выбранные приложения, языки и фильтр по активности. Причины
              отсечения — ниже.
            </div>
          )}

          {estimate.inactive_targets.length > 0 && (
            <div className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <strong className="block">Приложения без интеграции</strong>
              {estimate.inactive_targets.join(', ')} — получателей у них не будет.
            </div>
          )}

          <Breakdown
            title="По приложениям"
            entries={Object.entries(estimate.by_target)}
            emptyHint="Ни одно приложение не выбрано"
          />
          <Breakdown
            title="По языкам"
            entries={Object.entries(estimate.by_language).map(
              ([code, count]) =>
                [languages.find((item) => item.code === code)?.name ?? code, count] as [
                  string,
                  number,
                ],
            )}
            emptyHint="Ни один язык не выбран"
          />

          {(estimate.excluded_no_language > 0 || estimate.excluded_no_consent > 0) && (
            <div className="mt-3 border-t border-gray-200 pt-3 text-sm">
              <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">Исключены</p>

              {estimate.excluded_no_language > 0 && (
                <p className="mb-1 text-[#2d2c2c]">
                  {formatPushNumber(estimate.excluded_no_language)} — язык приложения не
                  выбран в рассылке
                </p>
              )}

              {estimate.excluded_no_consent > 0 && (
                <p className="text-[#2d2c2c]">
                  {formatPushNumber(estimate.excluded_no_consent)} — нет согласия на
                  рекламные уведомления
                  {notificationType?.requires_consent && (
                    <span className="mt-1 block text-xs text-gray-500">
                      Тип «{notificationType.name}» требует согласия. Согласие даётся
                      в приложении и не может быть получено задним числом, поэтому у
                      установок, перенесённых из старого сервиса, его нет.
                    </span>
                  )}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Breakdown({
  title,
  entries,
  emptyHint,
}: {
  title: string;
  entries: [string, number][];
  emptyHint: string;
}) {
  return (
    <div className="mb-3">
      <p className="mb-1 text-xs uppercase tracking-wide text-gray-500">{title}</p>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-400">{emptyHint}</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {entries.map(([label, count]) => (
            <PushBadge key={label}>
              {label}: {formatPushNumber(count)}
            </PushBadge>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Отправка                                                                    */
/* -------------------------------------------------------------------------- */

/** Короткий список зон вместо полного справочника: маркетинг работает
 *  в нескольких, а выпадающий список из шестисот значений бесполезен. */
const TIMEZONES = [
  'Europe/Moscow',
  'Europe/Kaliningrad',
  'Asia/Yekaterinburg',
  'Asia/Novosibirsk',
  'Asia/Vladivostok',
  'Europe/Lisbon',
  'America/Sao_Paulo',
  'Asia/Shanghai',
  'UTC',
];

/**
 * Панель отправки.
 *
 * Массовая отправка требует явного подтверждения с числом получателей.
 * Одним кликом её запускать нельзя, а «отменить» после запуска уже не
 * получится.
 *
 * Кнопки отправки скрыты у тех, у кого нет права send. Это удобство
 * интерфейса, а не защита: то же право проверяется на бэкенде.
 */
export function PushSendPanel({
  campaign,
  estimate,
  problems,
  dirty,
  canSend,
  saving,
  testDevices,
  timezone,
  onSave,
  onSend,
  onSchedule,
  onCancel,
  onTest,
}: {
  campaign?: PushCampaign;
  estimate?: PushAudienceEstimate;
  problems: DraftProblem[];
  dirty: boolean;
  canSend: boolean;
  saving: boolean;
  testDevices: PushTestDevice[];
  timezone: string;
  onSave: () => Promise<void>;
  onSend: () => Promise<void>;
  onSchedule: (isoDateTime: string, timezone: string) => Promise<void>;
  onCancel: () => Promise<void>;
  onTest: (deviceIds: number[]) => Promise<number>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState<number[]>([]);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduleTz, setScheduleTz] = useState(timezone);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const editable = !campaign || campaign.status === 'draft' || campaign.status === 'scheduled';
  const cancellable =
    campaign && ['draft', 'scheduled', 'queued'].includes(campaign.status);
  const ready = problems.length === 0 && !dirty && Boolean(campaign);

  async function guard(action: () => Promise<void>) {
    setError('');
    setBusy(true);
    try {
      await action();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (actionError: any) {
      setError(actionError?.message || 'Не удалось выполнить действие');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={CARD}>
      <h2 className="mb-3 text-base font-semibold text-[#2d2c2c]">Отправка</h2>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}
      {notice && (
        <div className="mb-3 rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</div>
      )}

      {problems.length > 0 && (
        <div className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
          <strong className="block">Рассылка не готова</strong>
          <ul className="list-disc pl-4">
            {problems.slice(0, 6).map((problem, index) => (
              <li key={index}>
                {problem.language ? `[${problem.language}] ` : ''}
                {problem.message}
              </li>
            ))}
          </ul>
          {problems.length > 6 && <p className="mt-1">и ещё {problems.length - 6}</p>}
        </div>
      )}

      {dirty && problems.length === 0 && (
        <div className="mb-3 rounded-lg bg-sky-50 p-3 text-sm text-sky-800">
          Есть несохранённые изменения. Сохраните, чтобы отправить.
        </div>
      )}

      {campaign?.scheduled_at && campaign.status === 'scheduled' && (
        <div className="mb-3 rounded-lg bg-sky-50 p-3 text-sm text-sky-800">
          <strong className="block">Запланирована</strong>
          {formatPushDate(campaign.scheduled_at)}
          {campaign.schedule_timezone ? ` (${campaign.schedule_timezone})` : ''}. Аудитория
          будет посчитана в момент запуска, а не сейчас.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {editable && (
          <Button isWhite disabled={saving || busy} onClick={() => void guard(onSave)}>
            {saving ? 'Сохранение…' : 'Сохранить черновик'}
          </Button>
        )}

        <Button
          isWhite
          disabled={busy || problems.length > 0 || dirty || testDevices.length === 0}
          onClick={() => setTesting(true)}
          className={testDevices.length === 0 ? 'opacity-50' : ''}
        >
          Тестовая отправка
        </Button>

        {canSend && editable && (
          <>
            <Button isWhite disabled={!ready || busy} onClick={() => setScheduling(true)}>
              Запланировать
            </Button>
            <Button disabled={!ready || busy} onClick={() => setConfirming(true)}>
              Отправить
            </Button>
          </>
        )}

        {canSend && cancellable && campaign?.status !== 'draft' && (
          <Button variant="danger" disabled={busy} onClick={() => void guard(onCancel)}>
            Отменить отправку
          </Button>
        )}
      </div>

      {testDevices.length === 0 && (
        <p className="mt-2 text-xs text-gray-500">
          Тестовая отправка недоступна: нет привязанных устройств. Привяжите телефон на
          вкладке «Тестовые устройства».
        </p>
      )}

      {!canSend && (
        <p className="mt-3 text-xs text-gray-500">
          У вас нет права на отправку рассылок. Подготовить и сохранить черновик можно,
          отправить — нет.
        </p>
      )}

      {/* Подтверждение массовой отправки */}
      <MyModal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title="Отправить рассылку?"
        size="sm"
      >
        <p className="text-sm text-[#2d2c2c]">
          Получателей по расчёту:{' '}
          <strong>
            {formatPushNumber(estimate?.total ?? campaign?.estimated_audience ?? null)}
          </strong>
        </p>
        <p className="mt-2 text-sm text-gray-500">
          Точное число будет зафиксировано в момент запуска и может отличаться: за время
          подготовки появились новые установки, а часть старых отключилась.
        </p>
        <p className="mt-2 text-sm text-gray-500">Отменить отправку после запуска нельзя.</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button isWhite onClick={() => setConfirming(false)}>
            Отмена
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              void guard(async () => {
                await onSend();
                setConfirming(false);
              })
            }
          >
            Да, отправить
          </Button>
        </div>
      </MyModal>

      {/* Планирование */}
      <MyModal
        isOpen={scheduling}
        onClose={() => setScheduling(false)}
        title="Запланировать отправку"
        size="sm"
      >
        <label className={FIELD_LABEL}>
          Дата и время <span className="text-red-500">*</span>
        </label>
        <input
          type="datetime-local"
          value={scheduleAt}
          onChange={(event) => setScheduleAt(event.target.value)}
          className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
        />

        <div className="mt-3">
          <label className={FIELD_LABEL}>Часовой пояс</label>
          <Select
            options={TIMEZONES.map((zone) => ({ value: zone, label: zone }))}
            optionSelected={{ value: scheduleTz, label: scheduleTz }}
            isVisible={false}
            isClearable={false}
            customStyles={selectControlStyles}
            onSelected={(option: SelectOption | null) =>
              option && setScheduleTz(String(option.value))
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            Одна зона на всю рассылку. Отправка по местному времени получателя не
            поддерживается.
          </p>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button isWhite onClick={() => setScheduling(false)}>
            Отмена
          </Button>
          <Button
            disabled={!scheduleAt || busy}
            onClick={() =>
              void guard(async () => {
                await onSchedule(new Date(scheduleAt).toISOString(), scheduleTz);
                setNotice('Рассылка запланирована.');
                setScheduling(false);
              })
            }
          >
            Запланировать
          </Button>
        </div>
      </MyModal>

      {/* Тестовая отправка */}
      <MyModal
        isOpen={testing}
        onClose={() => setTesting(false)}
        title="Тестовая отправка"
        size="sm"
      >
        <p className="text-sm text-gray-500">
          Тест игнорирует фильтры аудитории, включая согласие на рекламу, и не попадает
          в статистику рассылки.
        </p>

        <div className="mt-3">
          {testDevices.map((device) => (
            <label key={device.id} className="flex cursor-pointer gap-2 py-1">
              <input
                type="checkbox"
                className="mt-1"
                checked={selectedDevices.includes(device.id)}
                onChange={() =>
                  setSelectedDevices((current) =>
                    current.includes(device.id)
                      ? current.filter((id) => id !== device.id)
                      : [...current, device.id],
                  )
                }
              />
              <span>
                <span className="text-sm text-[#2d2c2c]">
                  {device.label || device.app_target}
                </span>
                <span className="block text-xs text-gray-500">
                  {device.app_target} · {device.language} ·{' '}
                  {device.app_version || 'версия неизвестна'}
                </span>
              </span>
            </label>
          ))}
        </div>

        <p className="mt-2 text-xs text-gray-500">
          Если ничего не выбрано, тест уйдёт на все ваши устройства, подходящие под
          выбранные приложения.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <Button isWhite onClick={() => setTesting(false)}>
            Отмена
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              void guard(async () => {
                const recipients = await onTest(selectedDevices);
                setNotice(
                  `Тестовое уведомление отправлено на ${formatPushNumber(recipients)} устройств.`,
                );
                setTesting(false);
              })
            }
          >
            Отправить тест
          </Button>
        </div>
      </MyModal>
    </div>
  );
}
